import React, { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';

import {
    equipmentApi,
    removeShortInventoryFromCache,
    updateShortInventoryInCache,
} from '@entities/equipment';
import {
    EquipmentInventoryStatus,
    FullInventory,
    ShortInventoryDto,
    equipmentInventoryApi,
    equipmentInventoryQueryKeys,
    useEquipmentInventoryStatuses,
} from '@entities/equipmentInventory';
import { DateHelpers } from '@shared/helpers';

import { EquipmentDataQueryCache } from './types';
import {
    buildInventoryPatch,
    getFilesValidationError,
    getInventoryLogFromResponse,
    toDisplayDate,
    toRequestDate,
} from '../lib/helpers';

const PREVIEW_URL_FIELD = 'preview_url';
const FREE_DATE_FIELD = 'free_date';

export const useEquipmentInventoryModal = (
    isOpen: boolean,
    inventoryGuid: string | null,
    warehouseId: number,
    onClose: () => void
) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { inventoryStatusesData } = useEquipmentInventoryStatuses();
    const equipmentDataQueryKey = equipmentApi.getEquipmentDataQueryOptions().queryKey;

    const inventoryQuery = useQuery({
        ...equipmentInventoryApi.viewInventoryQueryOptions({
            inventoryGuid: inventoryGuid ?? '',
        }),
        enabled: isOpen && Boolean(inventoryGuid),
    });

    const inventory = inventoryQuery.data;
    const [form, setForm] = useState({
        title: '',
        previewUrl: null as string | null,
        inventoryNumber: '',
        purchaseDate: '',
        amount: '1',
        unit: t('Unit.emptyValue.text'),
        price: '0',
        cost: '0',
        description: '',
        status: 1 as EquipmentInventoryStatus,
        freeDate: '',
    });
    const [imageError, setImageError] = useState('');
    const dateInputError: { isError: boolean; errorMessage: string | null } = {
        isError: false,
        errorMessage: null,
    };

    const setPurchaseDateInputValue: React.Dispatch<React.SetStateAction<string>> = (value) => {
        setForm((previous) => ({
            ...previous,
            purchaseDate: typeof value === 'function' ? value(previous.purchaseDate) : value,
        }));
    };

    const setFreeDateInputValue: React.Dispatch<React.SetStateAction<string>> = (value) => {
        setForm((previous) => ({
            ...previous,
            freeDate: typeof value === 'function' ? value(previous.freeDate) : value,
        }));
    };

    const purchaseDateOptions = {
        startDate:
            form.purchaseDate ||
            DateHelpers.dateFormatter({ date: new Date(), format: 'DD.MM.YYYY' }),
        singleDatePicker: true,
        showDropdowns: true,
        autoApply: true,
    };

    const freeDateOptions = {
        startDate:
            form.freeDate ||
            DateHelpers.dateFormatter({
                date: DateHelpers.today().add(1, 'day').toDate(),
                format: 'DD.MM.YYYY',
            }),
        singleDatePicker: true,
        showDropdowns: true,
        autoApply: true,
        isInvalidDate: (date: Moment) => date.isBefore(moment().startOf('day'), 'day'),
    };

    useEffect(() => {
        if (!inventory) {
            return;
        }

        setForm({
            title: inventory.title,
            previewUrl: inventory.previewUrl,
            inventoryNumber: inventory.inventoryNumber ?? '',
            purchaseDate: toDisplayDate(inventory.purchaseDate),
            amount: String(inventory.amount).replace('.', ','),
            unit: inventory.unit || t('Unit.emptyValue.text'),
            price: String(inventory.price).replace('.', ','),
            cost: String(inventory.cost).replace('.', ','),
            description: inventory.description ?? '',
            status: inventory.status,
            freeDate: toDisplayDate(inventory.freeDate),
        });
    }, [inventory, t]);

    const invalidateTotals = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
            }),
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({ warehouseId }).queryKey,
            }),
        ]);
    };

    const addHistoryLogFromResponse = (response: unknown) => {
        if (!inventoryGuid) {
            return;
        }

        const log = getInventoryLogFromResponse(response);

        if (!log) {
            return;
        }

        void queryClient.invalidateQueries({
            queryKey: equipmentInventoryQueryKeys.logsRoot(inventoryGuid),
        });
    };

    const updateInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.updateInventory,
        onSuccess: async (data) => {
            const { response } = data.data;

            addHistoryLogFromResponse(response);

            if (response && typeof response === 'object') {
                setForm((previous) => ({
                    ...previous,
                    ...('cost' in response
                        ? { cost: String(response.cost).replace('.', ',') }
                        : {}),
                    ...('price' in response
                        ? { price: String(response.price).replace('.', ',') }
                        : {}),
                }));
            }

            await invalidateTotals();
        },
    });

    const updateFreeDateMutation = useMutation({
        mutationFn: equipmentInventoryApi.updateInventoryFreeDate,
    });

    const uploadPhotosMutation = useMutation({
        mutationFn: equipmentInventoryApi.uploadInventoryPhotos,
        onSuccess: (data) => {
            addHistoryLogFromResponse(data.data.response);

            const photos = data.data.response.photos ?? [];
            const nextPreviewUrl = photos[photos.length - 1]?.original_url ?? null;

            if (!nextPreviewUrl || !inventoryGuid) {
                return;
            }

            setForm((previous) => ({
                ...previous,
                previewUrl: nextPreviewUrl,
            }));

            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                updateShortInventoryInCache({
                    previous,
                    warehouseId,
                    inventoryGuid,
                    patch: { [PREVIEW_URL_FIELD]: nextPreviewUrl },
                })
            );
        },
    });

    const updateStatusMutation = useMutation({
        mutationFn: equipmentInventoryApi.updateInventoryStatus,
        onSuccess: async (data) => {
            addHistoryLogFromResponse(data.data.response);
            await invalidateTotals();
        },
    });

    const removeInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.removeInventory,
        onSuccess: async (_data, variables) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                removeShortInventoryFromCache({
                    previous,
                    warehouseId,
                    inventoryGuid: variables.inventoryGuid,
                })
            );
            await invalidateTotals();
            onClose();
        },
    });

    const saveField = (
        field: keyof FullInventory | 'inventoryNumber' | 'purchaseDate',
        bodyField: string,
        value: string | number | null,
        shortPatch?: Partial<ShortInventoryDto>
    ) => {
        if (!inventoryGuid) {
            return;
        }

        updateInventoryMutation.mutate({
            inventoryGuid,
            body: buildInventoryPatch(bodyField, value),
        });

        if (shortPatch) {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                updateShortInventoryInCache({
                    previous,
                    warehouseId,
                    inventoryGuid,
                    patch: shortPatch,
                })
            );
        }
    };

    const saveFreeDate = (date: string) => {
        if (!inventoryGuid) {
            return;
        }

        const requestDate = date ? toRequestDate(date) : null;

        updateFreeDateMutation.mutate({
            inventoryGuid,
            date: requestDate,
        });

        queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
            updateShortInventoryInCache({
                previous,
                warehouseId,
                inventoryGuid,
                patch: { [FREE_DATE_FIELD]: requestDate ?? undefined },
            })
        );
    };

    const savePurchaseDate = (date: string) => {
        setPurchaseDateInputValue(date);
        saveField('purchaseDate', 'purchase_date', date ? toRequestDate(date) : '');
    };

    const saveStatus = (status: EquipmentInventoryStatus) => {
        if (!inventoryGuid || status === form.status) {
            return;
        }

        setForm((previous) => ({
            ...previous,
            status,
            freeDate: status === 0 ? previous.freeDate : '',
        }));

        updateStatusMutation.mutate({ inventoryGuid, status });

        queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
            updateShortInventoryInCache({
                previous,
                warehouseId,
                inventoryGuid,
                patch: {
                    status,
                    [FREE_DATE_FIELD]: undefined,
                },
            })
        );

        if (status !== 0) {
            saveFreeDate('');
        }
    };

    const uploadPhotos = (files: File[]) => {
        if (!inventoryGuid) {
            return;
        }

        const error = getFilesValidationError(files, t);

        if (error) {
            setImageError(error);

            return;
        }

        setImageError('');
        uploadPhotosMutation.mutate({ inventoryGuid, files });
    };

    const removeInventory = () => {
        if (!inventoryGuid) {
            return;
        }

        removeInventoryMutation.mutate({ inventoryGuid });
    };

    return {
        t,
        inventoryStatusesData,
        imageError,
        dateInputError,
        setPurchaseDateInputValue,
        setFreeDateInputValue,
        purchaseDateOptions,
        freeDateOptions,
        saveField,
        saveStatus,
        uploadPhotos,
        removeInventory,
        inventoryQuery,
        form,
        setForm,
        inventory,
        savePurchaseDate,
        saveFreeDate,
        removeInventoryMutation,
        uploadPhotosMutation,
    };
};
